import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { Global } from "../classes/Global";
import { Router } from "@angular/router";
import { BaseComponent } from '../classes/BaseComponent';
import { PriceHelper } from '../helpers/PriceHelper';
import { ListHelper } from '../helpers/ListHelper';
import { Config } from '../classes/Config';
import { WishlistService } from '../wishlist.service';
import { StringHelper } from '../helpers/StringHelper';
import { AuthenticationService } from '../authentication.service';
import { LoginHelper } from '../helpers/LoginHelper';
import { ToastService } from '../toast/toast.service';

@Component({
  selector: 'app-grid-item',
  templateUrl: './grid-item.component.html',
  styleUrls: ['./grid-item.component.scss'],
  providers: [ WishlistService, AuthenticationService ]
})
export class GridItemComponent extends BaseComponent implements OnInit, OnChanges {

  schema;
  id;
  highResImageLoaded: boolean = false;
  showPlaceHolder: boolean = false;

  imageContainerClass: string;
  imageClass: string;
  footerClass: string;
  gridItemClass: string;
  detailViewUrl: string = "";

  // Cached list states - updated only when item changes, not on every change detection
  inWishlist: boolean = false;
  inOwnlist: boolean = false;
  inBlocklist: boolean = false;

  @Input() item;
  @Input() priceHint;
  @Input() isCarouselItem: boolean;
  @Input() showQuality = true;
  @Input() small = false;
  @Input() store;
  @Input() media = "";
  @Input() showFooterTop: boolean = true;
  @Input() disableListActionSafety: boolean = false;

  @Output() onItemClicked = new EventEmitter();
  @Output() onWishlistClicked = new EventEmitter();
  @Output() onOwnlistClicked = new EventEmitter();
  @Output() onBlocklistClicked = new EventEmitter();
  @Output() onRemoveFromWishlistClicked  = new EventEmitter();
  @Output() onRemoveFromOwnlistClicked  = new EventEmitter();
  @Output() onRemoveFromBlocklistClicked  = new EventEmitter();

  // Delay for safety refresh after optimistic UI update (in ms)
  private static SAFETY_REFRESH_DELAY = 2500;

  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private toastService: ToastService
  ) {
    super();
  }

  ngOnInit() {
    if (this.item) {
      this.id = this.item.idInStore || this.item.itemID;
      this.gridItemClass = this.getGridItemClass();
      this.imageContainerClass = this.getImageContainerClass();
      this.imageClass = this.getImageClass();
      this.footerClass = this.getFooterClass();
      this.buildSchema();
      this.updateListStates();
      this.detailViewUrl = this.buildDetailViewUrl();
    }
    if (!this.store) {
      this.store = Global.getStore();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if ((changes.item && changes.item.currentValue) || (changes.store && this.item) || (changes.media && this.item)) {
      this.updateListStates();
      this.detailViewUrl = this.buildDetailViewUrl();
    }
  }

  /** Updates cached list states - call this when item changes or after list modifications */
  updateListStates(): void {
    if (!this.item) {
      this.inWishlist = false;
      this.inOwnlist = false;
      this.inBlocklist = false;
      return;
    }
    this.inWishlist = ListHelper.isItemInWishlist(this.item, this.store);
    this.inOwnlist = ListHelper.isItemInOwnlist(this.item);
    this.inBlocklist = ListHelper.isItemInBlocklist(this.item, this.store);
  }

  /**
   * Schedules a safety refresh after optimistic UI update.
   * If the actual state differs from expected after the delay, shows an error toast.
   */
  private scheduleSafetyRefresh(expectedWishlist: boolean, expectedOwnlist: boolean, expectedBlocklist: boolean): void {
    if (this.disableListActionSafety) {
      return;
    }
    setTimeout(() => {
      // Re-check the actual state from ListHelper
      const actualWishlist = ListHelper.isItemInWishlist(this.item, this.store);
      const actualOwnlist = ListHelper.isItemInOwnlist(this.item);
      const actualBlocklist = ListHelper.isItemInBlocklist(this.item, this.store);

      // Check if any expected state doesn't match actual state
      let hasError = false;
      if (expectedWishlist !== this.inWishlist && actualWishlist !== expectedWishlist) {
        // Wishlist action failed - revert to actual state
        this.inWishlist = actualWishlist;
        hasError = true;
      }
      if (expectedOwnlist !== this.inOwnlist && actualOwnlist !== expectedOwnlist) {
        // Ownlist action failed - revert to actual state
        this.inOwnlist = actualOwnlist;
        hasError = true;
      }
      if (expectedBlocklist !== this.inBlocklist && actualBlocklist !== expectedBlocklist) {
        // Blocklist action failed - revert to actual state
        this.inBlocklist = actualBlocklist;
        hasError = true;
      }

      if (hasError) {
        this.toastService.error(this.getString('listActionFailed'));
      }
    }, GridItemComponent.SAFETY_REFRESH_DELAY);
  }

  buildSchema() {
    if (!this.item) {
      return;
    }

    const price = PriceHelper.formatPrice(this.item[PriceHelper.getCompletePriceKey("", this.priceHint, this.item, false)], this.item.currency) || "0";

    this.schema = {
      '@context': 'http://schema.org',
      '@type': Global.getSchemaType(this.item.itemType),
      'name': this.item.title,
      'dateCreated': this.item.releaseDate,
      'image': this.getImageSrc(),
      'genre': this.item.primaryGenreName,
      'productionCompany': this.item.studio,
      'duration': this.getRunningTime(),
      'contentRating': this.item.advisoryRating,
      'offers': {
        '@type': 'Offer',
        'category': Global.getSchemaType(this.item.itemType),
        'availableDeliveryMethod': 'http://purl.org/goodrelations/v1#DeliveryModeDirectDownload',
        'name': this.item.title,
        'image': this.getImageSrc(),
        'url': this.item.productPageUrl,
        'eligibleRegion': Global.getCountry(),
        'offeredBy': {
          '@type': 'Organization',
          'name': Global.getStore()
        },
        'seller': {
          '@type': 'Organization',
          'name': Global.getStore()
        },
        'price': parseFloat(price.replace(/[^0-9^\.,]/g, '').replace(',','.')),
        'priceCurrency': this.item.currency
      }
    };

    if(this.item.itemType == 'album' || this.item.itemType == 'single') {
      this.schema["byArtist"] = {
        "@type": "MusicGroup",
        "name": this.item.artist
      }
    }

    if(this.item.itemType == 'album') {
      this.schema['albumReleaseType'] = "http://schema.org/AlbumRelease";
      this.schema["albumProductionType"] = "http://schema.org/StudioAlbum";
    }

    if(this.item.itemType == 'ebook') {
      this.schema['bookFormat'] = 'http://schema.org/EBook';
      this.schema['author'] = {'@type':'Person', 'name': this.item.artist};
    }

    if(this.item.itemType == 'app' || this.item.itemType == 'macapp') {
      this.schema['applicationCategory'] = this.item.primaryGenreName;

      if(this.item.plattform != null) {
        switch(this.item.plattform) {
          case 1:
          this.schema['availableOnDevice'] = 'iPhone';
          this.schema['operatingSystem'] = 'iOS';
          break;
          case 2:
          this.schema['availableOnDevice'] = 'iPad';
          this.schema['operatingSystem'] = 'iOS';
          break;
          case 3:
          this.schema['availableOnDevice'] = 'Mac';
          this.schema['operatingSystem'] = 'MacOS';
          break;
          default:
            this.schema['operatingSystem'] = 'iOS & MacOS';
          break;
        }
      }

      this.schema['author'] = {
        '@type': 'Organization',
        'name': this.item.artist
      };
    }

    if(this.item.itemType == 'app' && this.item.userRating) {
      this.schema['aggregateRating'] = {
        '@type': 'AggregateRating',
        'ratingCount': this.item.userRatingCount,
        'worstRating': 1,
        'bestRating': 5,
        'ratingValue': this.item.userRating
      };
    }

    if(this.item.itemType == 'movie' && this.item.artist) {
      this.schema['director'] = {'@type':'Person', 'name': this.item.artist};
    }

  }

  getImageSrc() {
    if (!this.item) {
      return "";
    }
    if (!this.useIvan() || !this.storeIsItunes()) {
      return this.getHttpsImageUrl();
    }
    else if (this.useIvan() && this.storeIsItunes()) {
      return this.getItemImageUrl();
    }
    return "";
  }

  getHttpsImageUrl() {
    if (!this.item) {
      return "";
    }
    const url = this.item.imageLargeUrl || this.item.itemImageURL || this.item.imageMediumUrl || this.item.imageSmallUrl || '';
    return StringHelper.getImageHttpsUrl(url);
  }

  getRunningTime(): string {
    if (!this.item || !this.item.runningTime) {
      return "";
    }
    let runningTime = this.item.runningTime;
    //Sometimes, the runningTime for movies is in minutes, sometimes its in seconds...
    //If it's bigger than 500, we can say it's in seconds. 500 seconds = 8min
    if (this.item.itemType === "movie" && runningTime < 500) {
      //Do nothing, already in minutes
    }
    else {
      runningTime /= 60;
    }
    runningTime = Math.floor(runningTime);
    if (!isNaN(runningTime) && runningTime > 0) {
      return runningTime + "min";
    }
    return "";
  }

  /**
   * Builds the detail view URL. This method is called during initialization and when item changes.
   * The result is cached in detailViewUrl property to avoid recalculating on every change detection cycle.
   */
  private buildDetailViewUrl(): string {
    if (!this.item) {
      return "";
    }

    // Ensure id is set and valid
    if (!this.id) {
      this.id = this.item.idInStore || this.item.itemID;
    }

    if (!this.id) {
      return "";
    }

    let m = Global.getMediaTypeForDetailView();

    if (this.media !== "") {
      m = this.media;
    }

    if (m == Global.TV) {
      m = 'seasons';
    }

    let storeUrl = this.store;
    if (!storeUrl) {
      storeUrl = Global.getStore();
    }
    if(storeUrl == Global.GOOGLEPLAY){ storeUrl = "google-play"; }

    const mediaUrl = this.getString(m + 'Url');
    if (!mediaUrl) {
      return "";
    }

    const country = Global.getCountry();
    if (!country) {
      return "";
    }

    const title = this.item.title;
    if (!title) {
      return "";
    }

    return '/' + country + '/' + storeUrl + '/' + mediaUrl + '/' + this.id + '/' + StringHelper.slugify(title);
  }

  /**
   * Returns the cached detail view URL. Use this method in templates instead of calling buildDetailViewUrl() directly.
   */
  getDetailViewUrl(): string {
    return this.detailViewUrl || "";
  }

  getItemImageUrl(): string {
    if (!this.item || !this.id) {
      return "";
    }
    return Config.imageServerUrl + Global.getCountry() + "/" + this.id + "/400x400.jpg";
  }

  showRemovedItemPlaceholder(): boolean {
    return this.itemIsRemovedFromStore();
  }

  itemIsRemovedFromStore(): boolean {
    if (!this.item) {
      return false;
    }

    const itemStatus = this.item.itemStatus !== undefined && this.item.itemStatus !== null ? String(this.item.itemStatus) : "";
    if (itemStatus === "1") {
      return true;
    }

    const title = this.item.title || this.item.itemName || "";
    return typeof title === "string" && title.indexOf("Removed from iTunes") >= 0;
  }

  getRemovedItemTitle(): string {
    if (!this.item) {
      return "";
    }

    const title = this.item.title || this.item.itemName || "";
    return String(title)
      .replace(/^\[[^\]]*Removed from iTunes\]\s*/, "")
      .trim();
  }

  isLoggedIn(): boolean {
    return this.authenticationService.isLoggedIn();
  }

  /** @deprecated Use inWishlist property instead for template bindings */
  isInWishlist(): boolean {
    return this.inWishlist;
  }

  /** @deprecated Use inBlocklist property instead for template bindings */
  isInBlocklist(): boolean {
    return this.inBlocklist;
  }

  /** @deprecated Use inOwnlist property instead for template bindings */
  isInOwnlist(): boolean {
    return this.inOwnlist;
  }

  hasRectangleImage(): boolean {
    if (!this.item) {
      return false;
    }
    // Seasons are squared images on iTunes, but rectangle images on Vudu
    if (this.item.itemType == "season" && this.store == "vudu") {
      return true; // Use rectangle image for landscape seasons on Vudu
    }
    return this.item.itemType == "movie" || this.item.itemType == "ebook";
  }

  formatPrice(price: number, currency: string): string {
    if (price && currency) {
      return PriceHelper.formatPrice(price, currency);
    }
    else {
      return "";
    }
  }

  removeFromWishlist(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!this.isLoggedIn()) {
      LoginHelper.getInstance().requestLogin();
      return;
    }
    this.inWishlist = false; // Optimistic UI update
    this.scheduleSafetyRefresh(false, this.inOwnlist, this.inBlocklist);
    this.onRemoveFromWishlistClicked.emit();
  }

  addToWishlist(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!this.isLoggedIn()) {
      LoginHelper.getInstance().requestLogin();
      return;
    }
    this.inWishlist = true; // Optimistic UI update
    this.scheduleSafetyRefresh(true, this.inOwnlist, this.inBlocklist);
    this.onWishlistClicked.emit();
  }

  addToOwnlist(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!this.isLoggedIn()) {
      LoginHelper.getInstance().requestLogin();
      return;
    }
    this.inOwnlist = true; // Optimistic UI update
    this.scheduleSafetyRefresh(this.inWishlist, true, this.inBlocklist);
    this.onOwnlistClicked.emit();
  }

  removeFromOwnlist(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!this.isLoggedIn()) {
      LoginHelper.getInstance().requestLogin();
      return;
    }
    this.inOwnlist = false; // Optimistic UI update
    this.scheduleSafetyRefresh(this.inWishlist, false, this.inBlocklist);
    this.onRemoveFromOwnlistClicked.emit();
  }

  addToBlocklist(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!this.isLoggedIn()) {
      LoginHelper.getInstance().requestLogin();
      return;
    }
    this.inBlocklist = true; // Optimistic UI update
    this.scheduleSafetyRefresh(this.inWishlist, this.inOwnlist, true);
    this.onBlocklistClicked.emit();
  }

  removeFromBlocklist(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!this.isLoggedIn()) {
      LoginHelper.getInstance().requestLogin();
      return;
    }
    this.inBlocklist = false; // Optimistic UI update
    this.scheduleSafetyRefresh(this.inWishlist, this.inOwnlist, false);
    this.onRemoveFromBlocklistClicked.emit();
  }

  urlIsHomePage(): boolean {
    return this.router.url == "/";
  }

  getImageContainerClass(): string {
    if (!this.item) {
      return "image-container";
    }
    let classString =  this.hasRectangleImage() ? this.getRectangleImageClass() : this.getSquareImageClass();
    classString += " image-container";
    return classString;
  }

  getRectangleImageClass(): string {
    if (this.small) {
      return 'rectangle-image-small';
    }
    return this.isCarouselItem ? 'rectangle-image-carousel' : 'rectangle-image';
  }

  getSquareImageClass(): string {
    if (this.small) {
      return 'square-image-small';
    }
    return this.isCarouselItem ? 'square-image-carousel' : 'square-image';
  }

  getImageClass(): string {
    if (!this.item) {
      return 'item-image';
    }
    let classString = 'item-image ';
    if (this.hasRectangleImage()) {
      classString += this.getRectangleImageClass();
    }
    else {
      classString += this.getSquareImageClass();
    }
    if (this.item.itemType == "app") {
      classString += " rounded-top-border";
    }
    return classString;
  }

  getFooterClass(): string {
    if (this.small) {
      return 'grid-item-footer-small';
    }
    const classString = this.isCarouselItem ? 'grid-item-footer-carousel' : 'grid-item-footer';
    return classString;
  }

  getGridItemClass(): string {
    let classString = "";
    if (this.small) {
      classString = 'grid-item-small';
    }
    else {
      classString = this.isCarouselItem ? 'grid-item-carousel' : 'grid-item';
    }
    if (this.item && this.item.itemType == "app") {
      classString += " rounded-top-border";
    }
    return classString;
  }

  useIvan(): boolean {
    return Config.useIvan;
  }

  storeIsItunes(): boolean {
    return this.store == Global.ITUNES;
  }

  getImgAlt(): string {
    if (!this.item || !this.highResImageLoaded) { return ""; }
    return StringHelper.format(this.getString('buyXonY'), this.item.title, this.storeIsItunes() ? "iTunes" : "Amazon");
  }

}
